
import {
  Component, Ref, Watch, Prop, Mixins, Inject,
} from 'vue-property-decorator';
import ResponsivenessMixin from '@/features/common/mixins/responsivenessMixin';
import {
  CommercialInvoiceValidationState,
} from '@/features/shipment/helpers/commercialInvoiceValidation';
import { createPartsBook, getPartsBook } from '@/features/shipment/services/parts-book.services';
import {
  scrollToInPage,
  showErrorAlertDialog,
  showSuccessAlertDialog,
  getFullUrlWithOrigin,
  scrollToBottom,
} from '@/features/common/helpers';
import { getCustomsNotifications } from '@/features/notifications/helpers/customs';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import { VForm, VInput } from '@/features/common/types';
import {
  FormFieldError,
  validateAndScrollToError,
} from '@/features/common/helpers/scrollToError';
import {
  commercialInvoiceFormFields,
} from '@/features/shipment/constants/commercialInvoiceFormFields';
import { trackCreateFormError } from '@/features/common/helpers/tracked-events';
import {
  InProgress,
  PartItem,
  ShippingTips,
  TemporaryCustomsParts,
} from '@/features/shipment/types';
import PartsSummary from '@/features/shipment/components/commercialInvoice/PartsSummary.vue';
import PartsBook from '@/features/shipment/components/commercialInvoice/PartsBook.vue';
import PartsBookIconButton from '@/features/shipment/components/commercialInvoice/PartsBookIconButton.vue';
import RequiredFieldsWarning from '@/features/common/components/RequiredFieldsWarning.vue';
import AddPartInputs from '@/features/shipment/components/commercialInvoice/AddPartInputs.vue';
import { isEuDestination } from '@/features/shipment/helpers/euProductIdentifiers';
import { UserInfo } from '@/features/users/types';
import { UserPreferences } from '@/features/settings/types';
import { FormValidationType } from '../createShipment/ExtrasShipment.vue';

@Component({
  components: {
    PartsBook,
    PartsBookIconButton,
    PartsSummary,
    RequiredFieldsWarning,
    AddPartInputs,
  },
})
export default class DetailedInfoComponent extends Mixins(ResponsivenessMixin) {
  @Inject({
    from: 'commercialInvoiceValidationState',
    default: () => ({ attempted: true }),
  })
  private commercialInvoiceValidationState!: CommercialInvoiceValidationState;

  @Ref('formPartsInfo') formPartsInfo!: VForm;

  @Prop({ required: true }) shippingTips!: ShippingTips;

  private formData: TemporaryCustomsParts = {
    quantityUnits: '',
    unitValue: '',
    detailedDescription: '',
    countryOfOrigin: '',
    hsCode: '',
    quantity: '',
    merchantProductId: '',
    manufacturerProductId: '',
    standardProductId: '',
  };

  private formValidation: FormValidationType = {
    isValid: true,
    message: '',
    isHint: true,
  }

  private showPartsBook = false;

  private isPartsBookNoContentPrompt = false;

  private partsSummary: PartItem[] = [];

  private partsBookData: PartItem[] = [];

  private getFullUrlWithOrigin = getFullUrlWithOrigin;

  private isFormChanged = false;

  private formDataKey = 0;

  private usmcaFormLink = '/pdf/usmca-certificate-of-origin-form.pdf';

  get generalInfoForm(): VForm {
    return this.$store.state.shipment.generalInfoForm;
  }

  get isInProgressData(): number {
    return this.$store.getters.getIsInProgressData;
  }

  get inProgressData(): InProgress {
    return this.$store.getters.getInProgress;
  }

  get isValidFormQuery(): boolean {
    return this.$route.query.isValid !== 'false';
  }

  get userInfo(): UserInfo {
    return this.$store.getters.getUserInfo;
  }

  get userPreferences(): UserPreferences {
    return this.$store.getters.getUserPreferences;
  }

  get isAutomaticallyAddPart(): boolean {
    return this.userPreferences.shippingSettings.isAutomaticallyAddPackage;
  }

  get toAddress() {
    return this.$store.getters.getShipToInProgress;
  }

  get fromAddress() {
    return this.$store.getters.getShipFromInProgress;
  }

  get isShipmentToUS(): boolean {
    return this.toAddress?.countryCode === 'US' && this.fromAddress?.countryCode !== 'US';
  }

  get hasCanadianOriginParts(): boolean {
    return this.partsSummary.some((part) => part.countryOfOrigin === 'CA');
  }

  get showUSMCANotice(): boolean {
    return this.isShipmentToUS && this.hasCanadianOriginParts;
  }

  get isEuPidShipment(): boolean {
    return isEuDestination(this.toAddress?.countryCode);
  }

  get showEuPidNotice(): boolean {
    if (!this.isEuPidShipment) return false;
    return this.partsSummary.some(
      (part) => !part.merchantProductId || !part.manufacturerProductId,
    );
  }

  get hasProcessingDocuments(): boolean {
    return this.$store.getters.hasProcessingDocuments;
  }

  get hasDocumentsWithoutType(): boolean {
    return this.$store.getters.hasDocumentsWithoutType;
  }

  @Watch('formValidation', { deep: true })
  private hideValidationMessageAfterFewSeconds(message: FormValidationType) {
    if (message.isValid) return;
    setTimeout(() => {
      this.resetValidationMessage();
    }, 7000);
  }

  @Watch('isInProgressData', { immediate: true })
  private setPartsSummary(newValue) {
    if (!newValue) return;

    const { commercialInvoice } = this.inProgressData;
    if (!commercialInvoice) return;

    const { customsParts } = commercialInvoice;
    this.partsSummary = customsParts || [];

    if (!commercialInvoice.temporaryCustomsParts) return;
    const {
      quantity, quantityUnits: qtyUnits, unitValue, detailedDescription,
      countryOfOrigin, hsCode, merchantProductId, manufacturerProductId, standardProductId,
    } = commercialInvoice.temporaryCustomsParts;

    this.formData = {
      quantityUnits: qtyUnits || '',
      unitValue: unitValue || '',
      detailedDescription: detailedDescription || '',
      countryOfOrigin: countryOfOrigin || '',
      hsCode: hsCode || '',
      quantity: quantity || '',
      merchantProductId: merchantProductId || '',
      manufacturerProductId: manufacturerProductId || '',
      standardProductId: standardProductId || '',
    };
  }

  created(): void {
    eventBus.$on(eventNames.SHIPMENT_CLEAR_ALL_INPUTS, this.clearPartsSummary);
    eventBus.$on(eventNames.ADD_PARTS_BOOK_ITEM, this.addToPartsBook);
  }

  destroyed(): void {
    eventBus.$off(eventNames.SHIPMENT_CLEAR_ALL_INPUTS, this.clearPartsSummary);
    eventBus.$off(eventNames.ADD_PARTS_BOOK_ITEM);
  }

  async mounted(): Promise<void> {
    this.submitIfInvalidInProgress();
    const items = await getPartsBook();
    if (items !== null) {
      this.partsBookData = [...items];
    }
  }

  private clearPartsSummary() {
    this.partsSummary = [];
    this.formValidation = {
      isValid: true,
      message: '',
      isHint: true,
    };
    (this.$refs.formPartsInfo as HTMLFormElement).reset();
  }

  private updatePartsBook(partsBook: PartItem[]) {
    this.partsBookData = partsBook;
  }

  private selectPartItemFromPartsBook(partItem: PartItem & { nickname: string}) {
    this.formData = {
      quantityUnits: partItem.quantityUnits,
      unitValue: partItem.unitValue,
      detailedDescription: partItem.detailedDescription,
      countryOfOrigin: partItem.countryOfOrigin,
      hsCode: partItem.hsCode,
      quantity: partItem.quantity,
      merchantProductId: partItem.merchantProductId || '',
      manufacturerProductId: partItem.manufacturerProductId || '',
      standardProductId: partItem.standardProductId || '',
    };
    this.showPartsBook = false;

    this.$nextTick(() => {
      this.formDataKey += 1;

      this.$nextTick(() => {
        this.updateInProgressTempPartsData(true);
        (this.$refs.formPartsInfo as HTMLFormElement).resetValidation();
      });
    });
  }

  private async addPartSummary(data: PartItem): Promise<boolean> {
    if (this.partsSummary.length >= 20) {
      showErrorAlertDialog(this.$t('shipment.commercialInvoice.parts20Restriction') as string);
      return false;
    }

    this.commercialInvoiceValidationState.attempted = true;
    await this.$nextTick();

    const isValid = this.formPartsInfo.validate();
    if (!isValid) return false;

    this.partsSummary.push(data);
    this.clearData();
    this.syncPartsSummary();

    scrollToInPage(0, document.body.scrollHeight, true);
    return true;
  }

  private duplicatePartSummary(index?: number): void {
    if (this.partsSummary.length >= 20) {
      showErrorAlertDialog(this.$t('shipment.commercialInvoice.parts20Restriction') as string);
      return;
    }

    const targetIndex = typeof index === 'number' ? index : this.partsSummary.length - 1;

    const originalItem = this.partsSummary[targetIndex];
    const duplicatedItem = { ...originalItem };

    this.partsSummary.splice(targetIndex + 1, 0, duplicatedItem);
    this.syncPartsSummary();
  }

  private updatePartsSummary(data: PartItem & { index?: number }) {
    if (data.index || data.index === 0) {
      this.$set(this.partsSummary, data.index, data);
      this.syncPartsSummary();
    }
  }

  private deletePartItem(idx: number): void {
    if (idx || idx === 0) {
      this.partsSummary.splice(idx, 1);
      this.syncPartsSummary();
    }
  }

  private syncPartsSummary() {
    this.$store.commit('setPartsSummary', this.partsSummary);
    this.updateInProgressTempPartsData(true);
  }

  private clearData(): void {
    (this.$refs.formPartsInfo as HTMLFormElement).reset();
    // Hidden PID inputs are not registered with v-form and cannot be reset by it.
    this.formData = {
      quantityUnits: '',
      unitValue: '',
      detailedDescription: '',
      countryOfOrigin: '',
      hsCode: '',
      quantity: '',
      merchantProductId: '',
      manufacturerProductId: '',
      standardProductId: '',
    };
    this.commercialInvoiceValidationState.attempted = false;
    this.formValidation = {
      isValid: true,
      message: '',
      isHint: true,
    };
  }

  private preparePartItemData(): PartItem {
    const {
      quantity,
      unitValue,
      detailedDescription,
      hsCode,
      countryOfOrigin,
      quantityUnits: qtyUnits,
      merchantProductId,
      manufacturerProductId,
      standardProductId,
    } = this.formData;

    return {
      quantity: quantity ? +quantity : 0,
      unitValue: unitValue ? +unitValue : 0,
      detailedDescription: detailedDescription || '',
      hsCode: hsCode || '',
      countryOfOrigin: countryOfOrigin || '',
      quantityUnits: qtyUnits || '',
      merchantProductId: this.isEuPidShipment ? String(merchantProductId ?? '').trim() : '',
      manufacturerProductId: this.isEuPidShipment ? String(manufacturerProductId ?? '').trim() : '',
      standardProductId: this.isEuPidShipment ? String(standardProductId ?? '').trim() : '',
    };
  }

  private async submit(): Promise<void> {
    this.commercialInvoiceValidationState.attempted = true;
    await this.$nextTick();

    this.formValidation = {
      isValid: true,
      message: '',
      isHint: true,
    };

    if (!(await this.validateCommercialInvoiceForm())) return;

    if (this.isTempPartData()) {
      this.formPartsInfo.validate();
      this.$store.commit('toggleConfirmDialog', {
        show: true,
        btnText: this.$t('buttons.ok') as string,
        content: this.$t('shipment.commercialInvoice.existingTempPartWarning') as string,
        title: this.$t('alertDialog.areYouSure'),
      });
      eventBus.$once(eventNames.CONFIRMATION_DIALOG, () => {
        this.continueSubmit(true);
      });
      return;
    }

    this.continueSubmit(false);
  }

  private async addPartAutomatically() {
    const isFormValid = this.formPartsInfo.validate();

    if (this.isAutomaticallyAddPart && isFormValid) {
      await this.addPartSummary(this.preparePartItemData());
    }
  }

  private async validateCommercialInvoiceForm(): Promise<boolean> {
    const generalInfoValid = this.generalInfoForm.validate();

    if (!this.partsSummary.length) {
      await this.addPartAutomatically();
    }

    const isParts = this.partsSummary.length;
    let message = '';

    if (!isParts) {
      message = this.isAutomaticallyAddPart
        ? '' : this.$t('shipment.commercialInvoice.noPartsWarning') as string;
      this.formPartsInfo.validate();
    }

    if (this.hasDocumentsWithoutType) {
      eventBus.$emit(eventNames.VALIDATE_DOCUMENT_TYPES);
    }

    if (!generalInfoValid || !isParts) {
      const scrollMessage = this.showValidationError();
      this.formValidation = {
        isValid: false,
        message: scrollMessage || message,
        isHint: true,
      };
      return false;
    }

    if (this.hasDocumentsWithoutType) {
      this.formValidation = {
        isValid: false,
        message: this.$t('documents.errorMessages.documentTypeRequired') as string,
        isHint: false,
      };
      return false;
    }

    return true;
  }

  private showValidationError(): string | null {
    const errors: FormFieldError[] = [];

    const collectErrors = (form: VForm | null | undefined) => {
      if (!form || !form.inputs) return;
      form.inputs.forEach((input: VInput) => {
        if (input.hasError) {
          errors.push({ id: input.id || '', errorMessage: input.validations?.[0] });
        }
      });
    };

    collectErrors(this.generalInfoForm);
    collectErrors(this.formPartsInfo);

    errors.forEach((err) => {
      const msg = (err.errorMessage || 'unknown').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      trackCreateFormError(this.$gtag, {
        field: err.id,
        message: msg,
        step: '4',
      });
    });

    return validateAndScrollToError(errors, commercialInvoiceFormFields);
  }

  private isTempPartData() {
    const {
      quantity,
      unitValue,
      detailedDescription,
      countryOfOrigin,
      hsCode,
      merchantProductId,
      manufacturerProductId,
      standardProductId,
    } = this.formData;

    const hasBasicData = quantity || unitValue || detailedDescription || countryOfOrigin || hsCode;
    if (hasBasicData) return true;
    return this.isEuPidShipment
      && !!(merchantProductId || manufacturerProductId || standardProductId);
  }

  private async continueSubmit(isDiscardTempPackage: boolean) {
    if (isDiscardTempPackage) {
      this.clearData();
    }

    (this.$refs.formPartsInfo as HTMLFormElement).resetValidation();

    this.updateInProgressTempPartsData(true);

    const onNotificationsSubmit = async (): Promise<void> => {
      this.$router.push({ name: 'shipment-results' });
      this.$emit('next');
    };

    const { commercialInvoice } = this.inProgressData;
    getCustomsNotifications(
      commercialInvoice,
      this.shippingTips,
      onNotificationsSubmit,
    );
  }

  private showPartsBookModal() {
    this.isPartsBookNoContentPrompt = false;
    this.showPartsBook = true;
  }

  private async savePartsBookItem(itemData: PartItem) {
    const isValid = this.formPartsInfo.validate();

    if (!isValid) {
      this.isPartsBookNoContentPrompt = true;
      this.showPartsBook = true;
      return true;
    }

    const response = await createPartsBook(itemData);
    return response;
  }

  private addToPartsBook(partsBookResponse: PartItem | boolean) {
    if (typeof partsBookResponse === 'boolean') return;

    this.partsBookData = [
      partsBookResponse,
      ...this.partsBookData,
    ];

    showSuccessAlertDialog(this.$t('alertDialog.partsBookAdded') as string);
  }

  private updateInProgressTempPartsData(force = false, sync = true) {
    this.parseNumberToAbsoluteValue();

    if (!force && !this.isFormChanged) return;
    if (!this.inProgressData) return;

    const { commercialInvoice } = this.inProgressData;
    this.$store.commit('patchInProgress', {
      key: 'commercialInvoice',
      value: {
        ...commercialInvoice,
        customsParts: commercialInvoice
          ? commercialInvoice.customsParts || []
          : [],
        temporaryCustomsParts: { ...this.formData },
      },
    });

    if (!sync) return;
    this.$store.dispatch('syncInProgressData');
  }

  private parseNumberToAbsoluteValue() {
    for (const key in this.formData) {
      if (Object.prototype.hasOwnProperty.call(this.formData, key)) {
        const value = this.formData[key];
        if (['quantity', 'unitValue'].includes(key)) {
          if (value && !Number.isNaN(parseInt(value, 10))) {
            this.formData[key] = Math.abs(value);
          }
        }
      }
    }
  }

  private submitIfInvalidInProgress() {
    if (this.isValidFormQuery) return;

    this.validateCommercialInvoiceForm();
    this.$router.push({ query: {} });

    setTimeout(() => {
      scrollToBottom();
    }, 1200);
  }

  private resetValidationMessage() {
    this.formValidation = {
      isValid: true,
      message: '',
      isHint: true,
    };
  }
}
